import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { todayDateObject } from '../libs/dateTimeUtilities';
import { fetchViewSettings, saveViewSettings } from '../libs/listViewRequests';
import {
  filtersWithAdded,
  filtersWithUpdated,
  filtersWithout,
  noViewModeChoices,
  restoredViewSettings,
  serializedViewSettings,
  viewModeChoicesWith,
  viewModeOn
} from '../libs/listViewSettings';
import type { ViewModeChoices } from '../libs/listViewSettings';
import { theme } from '../../tailwind.config';
import type {
  ActiveFilter,
  ListViewDate,
  ListViewMode,
  ListViewScreenSize
} from '../listview/types';

/* The client-side state of a view that is worth remembering for the user, and
 * the round-trip that remembers it via /viewsettings: the filters, the
 * card/table mode, the sets toggle, whether past bookings are hidden on today,
 * the selected day and the auto-refresh interval.
 *
 * The card/table mode is the one setting the screen has a say in: it follows
 * the width the view is opened at, and a user who reaches for the toggle
 * overrules that for the screen size they are on, which is what is remembered.
 *
 * Restoring happens once, when the view opens. A first-time user has no
 * stored document and gets a 404 — which is why settingsSettled means "the
 * request came back", not "settings were restored". Anything waiting on these
 * settings (the bookings fetch waits for the day) would otherwise wait forever
 * on a brand new user.
 *
 * A stored day that was today when it was stored is resolved against the clock
 * at restore and then left alone: a user who left the view on today finds today
 * again, while a day they picked deliberately stays put — and an open view does
 * not roll over at midnight.
 *
 * Saving is fire-and-forget on every change, from the setters rather than from
 * an effect on the values: an effect would PUT the freshly restored document
 * straight back, and would PUT the defaults over the stored document while the
 * GET was still in flight.
 *
 * Hiding the bookings that have ended is stored as the user left it and
 * applied only while the view is on today (see ListView), so a user who hid
 * them yesterday finds today's past bookings hidden today, and a day in the
 * past always shows everything it held. */

interface ViewSettingsOptions {
  host: string;
  viewID: string;
}

interface MutableSettings {
  /* The user's own card/table choice per screen size (see listViewSettings) */
  viewModeChoices: ViewModeChoices;
  showChildren: boolean;
  /* The preference, not the list's state: it applies on today only */
  hidePastBookingsOnToday: boolean;
  activeFilters: ActiveFilter[];
  date: ListViewDate;
  refreshIntervalMinutes: number;
}

export interface ViewSettings {
  viewMode: ListViewMode;
  showChildren: boolean;
  hidePastBookingsOnToday: boolean;
  activeFilters: ActiveFilter[];
  date: ListViewDate;
  refreshIntervalMinutes: number;
  /* The /viewsettings request came back, whether or not it held anything */
  settingsSettled: boolean;
  setViewMode: (viewMode: ListViewMode) => void;
  setShowChildren: (showChildren: boolean) => void;
  setHidePastBookingsOnToday: (hidePastBookingsOnToday: boolean) => void;
  setDate: (date: ListViewDate) => void;
  setRefreshInterval: (refreshIntervalMinutes: number) => void;
  addFilter: (filter: ActiveFilter) => void;
  updateFilter: (filter: ActiveFilter) => void;
  removeFilter: (filterID: string) => void;
}

const initialSettings = (): MutableSettings => ({
  viewModeChoices: noViewModeChoices(),
  showChildren: false,
  hidePastBookingsOnToday: false,
  activeFilters: [],
  date: todayDateObject(),
  refreshIntervalMinutes: 0
});

const useViewSettings = ({ host, viewID }: ViewSettingsOptions): ViewSettings => {
  const [settings, setSettings] = React.useState(initialSettings);
  /* Which view's settings request has come back, rather than a bare flag, so
     switching to another view waits for that view's own settings */
  const [settingsSettledFor, setSettingsSettledFor] = React.useState<string | null>(
    null
  );
  const settingsSettled = settingsSettledFor === viewID;

  /* The same breakpoint the toolbar reflows at, so the view mode flips where
     the controls above it do */
  const isLargeScreen = useMediaQuery({ minWidth: theme.screens.md });
  const screenSize: ListViewScreenSize = isLargeScreen ? 'large' : 'small';
  /* Derived, not stored: the screen is not something the user set, so it is
     read on every render — which is how a resize reaches the view */
  const viewMode = viewModeOn(settings.viewModeChoices, screenSize);

  React.useEffect(() => {
    let cancelled = false;
    fetchViewSettings(host, viewID, (stored, error) => {
      if (cancelled) return;
      const restored = error ? {} : restoredViewSettings(stored, todayDateObject());
      setSettings((current) => ({
        viewModeChoices: restored.viewModeChoices || current.viewModeChoices,
        showChildren:
          restored.showChildren === undefined
            ? current.showChildren
            : restored.showChildren,
        hidePastBookingsOnToday:
          restored.hidePastBookingsOnToday === undefined
            ? current.hidePastBookingsOnToday
            : restored.hidePastBookingsOnToday,
        activeFilters: restored.activeFilters || current.activeFilters,
        date: restored.date || current.date,
        refreshIntervalMinutes:
          restored.refreshIntervalMinutes === undefined
            ? current.refreshIntervalMinutes
            : restored.refreshIntervalMinutes
      }));
      /* Batched with the update above, so the restored day is in place by the
         time anything reacts to the gate opening */
      setSettingsSettledFor(viewID);
    });
    return () => {
      cancelled = true;
    };
  }, [host, viewID]);

  const change = (patch: Partial<MutableSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (!settingsSettled) return;
    saveViewSettings(host, viewID, serializedViewSettings(next, todayDateObject()));
  };

  const addFilter = (filter: ActiveFilter) => {
    const activeFilters = filtersWithAdded(settings.activeFilters, filter);
    /* One filter per field: adding for the same field again is ignored */
    if (activeFilters === settings.activeFilters) return;
    change({ activeFilters });
  };

  return {
    viewMode,
    showChildren: settings.showChildren,
    hidePastBookingsOnToday: settings.hidePastBookingsOnToday,
    activeFilters: settings.activeFilters,
    date: settings.date,
    refreshIntervalMinutes: settings.refreshIntervalMinutes,
    settingsSettled,
    /* Recorded for the screen size it was picked on; the other size keeps
       whatever it was last told */
    setViewMode: (viewMode: ListViewMode) =>
      change({
        viewModeChoices: viewModeChoicesWith(
          settings.viewModeChoices,
          screenSize,
          viewMode
        )
      }),
    setShowChildren: (showChildren: boolean) => change({ showChildren }),
    setHidePastBookingsOnToday: (hidePastBookingsOnToday: boolean) =>
      change({ hidePastBookingsOnToday }),
    setDate: (date: ListViewDate) => change({ date }),
    setRefreshInterval: (refreshIntervalMinutes: number) =>
      change({ refreshIntervalMinutes }),
    addFilter,
    updateFilter: (filter: ActiveFilter) =>
      change({ activeFilters: filtersWithUpdated(settings.activeFilters, filter) }),
    removeFilter: (filterID: string) =>
      change({ activeFilters: filtersWithout(settings.activeFilters, filterID) })
  };
};

export default useViewSettings;
